import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Progress} from "@/components/ui/progress";
import {cn} from "@/lib/utils";

import {DAR_SEVERITY_LABELS, DAR_SEVERITY_ORDER} from "../lib";
import type {DashboardBreakdownRow, DashboardDarsBySeverity} from "../types";

type DarsSeverityBreakdownProps = {
  /** Backend may ship either a severity-keyed object or a generic row list. */
  data: DashboardDarsBySeverity | DashboardBreakdownRow[] | undefined;
};

const SEVERITY_PROGRESS_CLASS: Record<"red" | "yellow" | "green", string> = {
  red: "bg-rose-500/15 [&_[data-slot=progress-indicator]]:bg-rose-500",
  yellow: "bg-amber-400/20 [&_[data-slot=progress-indicator]]:bg-amber-400",
  green: "bg-emerald-500/15 [&_[data-slot=progress-indicator]]:bg-emerald-500",
};

const SEVERITY_DOT_CLASS: Record<"red" | "yellow" | "green", string> = {
  red: "bg-rose-500",
  yellow: "bg-amber-400",
  green: "bg-emerald-500",
};

function normalize(
  data: DashboardDarsBySeverity | DashboardBreakdownRow[] | undefined,
): DashboardDarsBySeverity {
  if (!data) return {red: 0, yellow: 0, green: 0};
  if (Array.isArray(data)) {
    const out = {red: 0, yellow: 0, green: 0};
    for (const row of data) {
      const key = (
        typeof row?.key === "string" ? row.key : typeof row?.label === "string" ? row.label : ""
      )
        .trim()
        .toLowerCase() as "red" | "yellow" | "green";
      if (key in out) {
        out[key] = typeof row?.count === "number" ? row.count : 0;
      }
    }
    return out;
  }
  return {red: data.red ?? 0, yellow: data.yellow ?? 0, green: data.green ?? 0};
}

/**
 * DAR severity breakdown. Always rendered in priority order Red > Yellow >
 * Green so users see escalation-worthy items first, even when counts are
 * low.
 */
export function DarsSeverityBreakdown({data}: DarsSeverityBreakdownProps) {
  const counts = normalize(data);
  const total = counts.red + counts.yellow + counts.green;

  return (
    <Card className="gap-3 py-4">
      <CardHeader>
        <CardTitle className="text-base font-bold tracking-tight">DARs by severity</CardTitle>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <p className="text-muted-foreground text-sm">
            No DARs recorded for this period.
          </p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {DAR_SEVERITY_ORDER.map((severity) => {
              const count = counts[severity];
              const pct = total > 0 ? (count / total) * 100 : 0;
              return (
                <li key={severity} className="flex flex-col gap-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm font-semibold">
                      <span
                        className={cn(
                          "inline-block size-2 rounded-full",
                          SEVERITY_DOT_CLASS[severity],
                        )}
                      />
                      {DAR_SEVERITY_LABELS[severity]}
                    </span>
                    <span className="text-muted-foreground tabular-nums text-xs">
                      {count.toLocaleString()}
                    </span>
                  </div>
                  <Progress
                    value={pct}
                    className={cn("h-1.5", SEVERITY_PROGRESS_CLASS[severity])}
                  />
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
